// src/components/inventory/TransferLoadPanels.js
// #517, Session 79.10. Gate docs/gates/Stede_PartialShipment_Gate_517.html,
// revision 2, decisions D48-D68, fully approved.
//
// ── What these two panels are, in plain words ──
//
// Dallas asked Houston for four hundred frames and Houston has one trailer.
// LOAD PANEL is where Houston says what is actually going on this truck, and
// the screen's whole job is to make "this is not all of it" an ordinary thing
// to say rather than an error to explain.
//
// ARRIVAL PANEL is the other end. Somebody at the far end says the load turned
// up and, only if something is wrong, says what was actually on it.
//
// ── The rule that shapes both of them ──
//
// THE ORDINARY WEDNESDAY IS TWO TAPS. Colby's own constraint: open, tap a card,
// type how many, done. Everything else on these panels is either derived or
// optional, and the discrepancy is ONE EXTRA TAP taken only when something is
// actually wrong. D63 calls that "the default must be silence", and it is how
// OS&D works on paper too — you write a note when there is something to note.

import React, { useState, useMemo, useEffect } from 'react';
import ActionButton from '../ActionButton';
import { useTier, controlHeight, tierFor, tierAtLeast } from '../../lib/tiers';
import {
  loadRef, loadableQty, standingAt, transferProgress,
  discrepancy, overCountRefusal, overCountAsk, nothingStandingToSend, nothingStandingMessage,
  canSeeLoadValue, loadValue, promisedDateFor,
  hubsWaitingFor, stopKey, driverChoices, driverPayload, DRIVER_SOMEONE_ELSE,
  transferHistory, standingSentence,
  // #729 -- the count-in heading's words, named by branch.
  countInHeading,
  // #665 D9/D12 and #690 D11 -- the rules the To load card shares with this sheet.
  defaultRoute, driverDefault, loaderNamesNobody, nextLoadSeq,
  // #699, gate 665 D28 -- the yard's default taker, by the same driver rule.
  presserDriver,
} from '../../lib/transferLoads';
import { promisedDayLabel } from '../../lib/branchTransfers';
// #682 / #702 -- who sees Correct on a history line, and how its numbers print.
import { canCorrectStep } from '../../lib/permissions';
import { qtyText } from '../../lib/corrections';
import { shortWhen } from './CorrectionSheet';
// #708 -- the one style for a count somebody confirms.
import { qtyBox, QTY_LABEL, QTY_BOX_CLASS } from '../../lib/qtyBox';

const money = (n) =>
  n == null ? null : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// 44px is the touch minimum — #513, and the standards floor this project has
// committed to in writing.
const TAP = 44;

// #632, Session 79.22b.1 -- THE COMMIT FLOOR MOVED AFTER THESE SCREENS WERE
// BUILT. 44 was right when it was chosen: it is Apple's 44pt guidance and what
// Session 79.6 built to. Then #417 D2 settled the commit floor at 48 at Session
// 79.15, and the transfer module was never swept back over. Nobody made a wrong
// call -- the floor moved and the screens did not.
//
// A CONTROL THAT FINISHES AN ACT TAKES THIS. The selects, the text boxes and
// the stop pickers stay at TAP: they are above the 40 minimum already, and
// growing them would move geometry this fix has no reason to touch. A button
// standing in the SAME ROW as a commit takes it too, so the pair matches.
const COMMIT = 48;

// #672, Session 79.22b.4 -- THE DRIVER'S DISCLOSURE ROWS, SIZED TO THE POINTER.
// "Change where it is handed over", "Who is driving?" and "Who is taking it?"
// were a literal 24px -- the MOUSE minimum -- on the driver's own phone sheets,
// in a file that already imports the floor. Measured at 375px: 24 against 40.
// Now 40 with a finger and 24 with a mouse, the text centred in the row.
function disclosureStyle(coarse) {
  const h = controlHeight(coarse, 'min');
  const pad = Math.max((h - 16) / 2, 0);
  return {
    fontSize: 12, color: '#5C5C5C', cursor: 'pointer', lineHeight: '16px',
    minHeight: h, boxSizing: 'border-box', paddingTop: pad, paddingBottom: pad,
  };
}

// #632, Session 79.22b.1 -- the count box was 35px on a phone against the 40 a
// finger needs. #708, Session 79.22b.4.3 -- and it was the faintest thing on the
// sheet, so the eye skipped the number it was there to check. Both now come from
// qtyBox() in src/lib/qtyBox.js: 48px, the green edge, the dark green number.

// #553 D10. The destination select holds branch ids as strings, so the
// road-spot entry needs a value that can never be one. Deliberately not '' —
// that already means "straight through".
const TO_PLACE = 'on_the_road';

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN 1 — HOUSTON SENDS WHAT FITS
// ─────────────────────────────────────────────────────────────────────────────
export function LoadPanel({
  // #671 -- `loadsReady` is false while THIS transfer's loads are still being
  // read. Defaults true so every other caller is unchanged.
  transfer, lines, loads, loadsReady = true, items, branchNames, myBranch, auth,
  fulfilmentSourced, isMobile, busy, onLoad, onCloseUnfilled,
  unitCostFor, hubs = [], drivers = [],
  // #628 half 2 -- a made-to-order load from the sending branch's shelf, and
  // what that shelf holds. Both come from the sheet, which owns the answer.
  fromShelf = false, shelfFor = null,
  // #690 -- company_settings.transfer_loader_model, read by the page.
  loaderModel = 'driver_loads',
}) {
  const { coarse } = useTier();   // #672
  const [qty, setQty] = useState({});
  const [truck, setTruck] = useState('');

  // #553 D12. `driver` is now ONLY the escape's typed name -- the picked
  // person is `driverPick`, which holds a user id or the escape sentinel.
  const [driver, setDriver] = useState('');
  const driverList = useMemo(
    () => driverChoices(drivers, auth?.user?.id),
    [drivers, auth?.user?.id],
  );

  // PREFILLED WITH WHOEVER IS PRESSING -- Colby specified this precisely:
  // "the picker pre fills with who ever opened the count in". So the common
  // case costs zero taps, and the picker is only opened when it is wrong.
  //
  // Falls back to "Nobody recorded" rather than to the first stranger on the
  // list: naming the wrong person is the defect this decision exists to fix.
  const [driverPick, setDriverPick] = useState('');
  const myId = auth?.user?.id == null ? null : String(auth.user.id);
  // #690, gate 665 D11 -- the default is the ONE driver rule the To load card
  // uses too, so the two cannot disagree. Under "Loaders load, drivers drive"
  // it is nobody, and the picker below is not offered at all.
  const loaderMode = loaderNamesNobody(loaderModel);
  useEffect(() => {
    const pick = driverDefault({ currentUserId: myId, drivers, loaderModel });
    if (pick) setDriverPick(pick);
  }, [myId, drivers, loaderModel]);

  const driverSummary = useMemo(() => {
    if (driverPick === DRIVER_SOMEONE_ELSE) return driver.trim() || 'someone else';
    const hit = driverList.find(d => d.id === driverPick);
    return hit ? (hit.isMe ? 'you' : hit.name) : 'optional';
  }, [driverPick, driver, driverList]);
  // ── THE HANDOVER IS THE DEFAULT, NOT THE EXCEPTION ──────────────────────
  //
  // #529, Colby at 79.10: "dropping off frames at the hub in austin is the
  // standard. houston running straight to dallas or san antonio is the rare
  // exception... a handoff happens 99.5% of the time."
  //
  // This used to start EMPTY, meaning straight-through, with the handover
  // hidden behind a collapsed "Dropping this somewhere on the way?" — so the
  // path taken almost every time cost an extra tap to find, and the rare one
  // was free. Backwards, and a tax on every load Houston sends.
  //
  // A hub that IS one of the two ends is not a handover, so it is skipped: a
  // load from Houston to Austin already ends at Austin.
  //
  // #665 D9 -- the rule now lives in defaultRoute(), shared with the To load
  // card, word for word what this line used to say.
  const hub = defaultRoute(transfer, hubs);
  const [toBranch, setToBranch] = useState(hub ? String(hub.id) : '');
  // D10. A sentinel rather than a branch id, because "a spot on the road" is
  // not a branch and must never collide with one. Numeric ids are stringified
  // for this select, so a non-numeric sentinel cannot be mistaken for one.
  const [toPlaceText, setToPlaceText] = useState('');
  const [mode, setMode] = useState(null);         // 'unfilled' | null
  const [unfilledReason, setUnfilledReason] = useState('');

  const originId = transfer.from_branch_id;
  const destId = transfer.to_branch_id;
  const atOrigin = String(myBranch) === String(originId);

  // WHERE THIS LOAD IS GOING. From the origin it is normally straight to the
  // destination, and the only reason to change it is the Austin case — dropping
  // at a staging point for somebody else to collect. From anywhere else there
  // is nothing to decide: you are moving it on to where it was always going.
  //
  // D10 — a road spot is NOT a branch id, so it never reaches this. `whereTo`
  // is the sentence a person reads before pressing Send, and it has to name
  // the place they typed rather than falling back to "there": the summary line
  // is the only confirmation of where this truck is actually going.
  const onTheRoad = atOrigin && toBranch === TO_PLACE;
  const effectiveTo = atOrigin && !onTheRoad ? (toBranch || destId) : destId;

  // #665 D12 -- the database's own MAX + 1, shared with the To load card.
  const seq = nextLoadSeq(loads);
  const ref = loadRef(transfer.transfer_number, seq);

  // The ceiling per line, which mirrors load_branch_transfer()'s own arithmetic
  // so the screen refuses an impossible number before the database does — with
  // a sentence somebody can act on rather than a raw exception.
  const ceilings = useMemo(() => {
    const out = {};
    for (const l of lines) {
      out[String(l.item_id)] = loadableQty({
        line: l, loads, itemId: l.item_id,
        fromBranchId: myBranch, originBranchId: originId, fromShelf,
      });
    }
    return out;
  }, [lines, loads, myBranch, originId, fromShelf]);

  // #628 half 2 -- FROM THE SHELF, THE NUMBER OFFERED IS WHAT IS THERE. The
  // ceiling is what is still owed (the database's rule), and the shelf can hold
  // less; offering the owed figure would suggest sending what does not exist.
  // A count can be wrong, so typing more is WARNED, never blocked.
  const shelfQty = (itemId) => (fromShelf && atOrigin && typeof shelfFor === 'function')
    ? Math.max(Number(shelfFor(itemId)) || 0, 0) : null;

  const going = lines.map(l => {
    const raw = qty[String(l.item_id)];
    const ceiling = ceilings[String(l.item_id)] || 0;
    const onShelf = shelfQty(l.item_id);
    const suggested = onShelf == null ? ceiling : Math.min(ceiling, onShelf);
    const n = raw === '' || raw == null ? suggested : Number(raw);
    return { ...l, __going: Number.isFinite(n) ? n : 0, __ceiling: ceiling, __suggested: suggested, __onShelf: onShelf };
  });
  const pastTheShelf = going.filter(l => l.__onShelf != null && l.__going > l.__onShelf);

  const totalGoing = going.reduce((n, l) => n + l.__going, 0);
  const over = going.find(l => l.__going > l.__ceiling);
  // #586, Session 79.21b -- NOTHING CAN BE CHOSEN IS NOT NOTHING CHOSEN. With
  // an empty rack every ceiling is zero, and the sheet asked the driver to
  // "type how many are going on this truck" when the most they could type was
  // nought. Gate 553's edge case draws this state: the sheet opens and says
  // what is true instead. Made goods and shelf goods are different sentences,
  // because "nothing has been made yet" is wrong about a shelf.
  const nothingStanding = nothingStandingToSend(going.map(l => l.__ceiling));
  const progress = transferProgress(lines);

  // What is still owed AFTER this load goes — the number the whole screen is
  // built around, because it is what makes a partial send an ordinary act
  // rather than something to apologise for.
  const owedAfter = Math.max(progress.requested - progress.shipped - totalGoing, 0);

  const payload = going
    .filter(l => l.__going > 0)
    .map(l => ({
      item_id: l.item_id,
      qty: l.__going,
      unit_cost: unitCostFor ? unitCostFor(l) : null,
      sourced: fulfilmentSourced || null,
    }));

  // D65 — A TEAM MEMBER NEVER SEES THE VALUE OF WHAT THEY ARE LOADING.
  // Colby asked outright and the answer is no. #515's D39 is this exact defect
  // one screen over, where the link to change a cost was gated and the field
  // itself was gated to nothing.
  const showValue = canSeeLoadValue(auth);
  const value = showValue
    ? loadValue(payload.map(p => ({ qty_loaded: p.qty, unit_cost_at_transfer: p.unit_cost })))
    : null;

  const nothingChosen = payload.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#3A3A3A' }}>
        {atOrigin ? 'What is going now?' : `Moving on what is standing at ${branchNames[String(myBranch)] || 'here'}`}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 0 /* #710 */ }}>
        <tbody>
          {going.map(l => {
            const name = items[String(l.item_id)]?.name || `Item ${l.item_id}`;
            // #701 -- the day in words, the way every other transfer screen
            // says it ("promised Fri, Oct 16"), not the stored 2026-10-16.
            const promised = promisedDayLabel(promisedDateFor(l, transfer));
            return (
              <tr key={l.item_id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                <td style={{ padding: '9px 0', verticalAlign: 'top' }}>
                  <div style={{ fontSize: 14, color: '#0F0F0F', fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>
                    {/* #701 -- no unit after the number: a stored unit never
                        turns plural ("0 box to send"), and the To load card
                        beside this already reads "{n} to send". */}
                    {l.__ceiling} to send
                    {l.__onShelf != null ? ` · ${l.__onShelf} on ${branchNames[String(originId)] || 'the'}'s shelf` : ''}
                    {promised ? ` · promised ${promised}` : ''}
                  </div>
                </td>
                <td style={{ padding: '9px 0 9px 12px', textAlign: 'right', verticalAlign: 'top', width: 116 }}>
                  <div style={QTY_LABEL}>Sending</div>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max={l.__ceiling}
                    value={qty[String(l.item_id)] ?? ''}
                    placeholder={String(l.__suggested)}
                    onChange={e => setQty(q => ({ ...q, [String(l.item_id)]: e.target.value }))}
                    className={QTY_BOX_CLASS}
                    style={qtyBox()}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* #628 half 2 -- sending more than the shelf shows is WARNED, never
          blocked: qty_on_hand is allowed below zero, and a count can be
          wrong. The database stops only at what is owed. */}
      {pastTheShelf.length > 0 && (
        <div style={{
          background: '#FDF3E3', borderLeft: '3px solid #EF9F27', borderRadius: 8,
          padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
        }}>
          {branchNames[String(originId)] || 'The'}’s shelf shows fewer than are going, so its count will read below zero. Check the shelf before it leaves.
        </div>
      )}

      {/* THE LINE THAT IS THE WHOLE FEATURE. The quantity box already accepted a
          smaller number; what is new is the sentence underneath saying what that
          MEANS — that the balance stays owed and the request stays open. D51,
          approved by Colby. */}
      {owedAfter > 0 && !nothingChosen && (
        <div style={{
          background: '#FDF3E3', borderLeft: '3px solid #EF9F27', borderRadius: 8,
          padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
        }}>
          <strong>{owedAfter} will still be owed after this goes.</strong>{' '}
          {branchNames[String(destId)] || 'They'} will see “{progress.shipped + totalGoing} of {progress.requested} sent · {owedAfter} still to come”, and the request stays open.
        </div>
      )}

      {/* D50 / D61 — the load names itself BEFORE you press send, so whoever is
          loading can write it on the paperwork and say it down a phone. */}
      {!nothingChosen && (
        <div style={{ fontSize: 12, color: '#5C5C5C' }}>
          {/* #671 -- "a brief … is correct; a confident wrong number is not."
              The reference is written on paperwork and read down a phone. */}
          This load <strong style={{ color: '#0F0F0F' }}>{loadsReady ? ref : '…'}</strong>
          {' · '}{branchNames[String(myBranch)] || 'here'} → {
            onTheRoad
              ? (toPlaceText.trim() || 'a spot on the road')
              : (branchNames[String(effectiveTo)] || 'there')
          }
        </div>
      )}

      {/* THE AUSTIN CASE. Only offered from the origin, and only as a deliberate
          choice — the default is straight through, which is what almost every
          load does. */}
      {/* THE ROUTE IS VISIBLE AND ALREADY ANSWERED. The driver reads one line
          and changes it only for the rare straight run — which is the inverse
          of what shipped this morning. */}
      {atOrigin && (
        <div style={{
          background: hub && toBranch ? '#EEF5FC' : '#F7F7F7',
          border: `1px solid ${hub && toBranch ? '#CFE2F5' : '#E8E8E8'}`,
          borderRadius: 8, padding: '10px 12px',
        }}>
          {/* #670, Session 79.22b.4 -- Colby: "yes the helper text for a handover
              should read specific to the handover." A road spot is not a branch
              id, so the branch lookup fell through to "a yard" -- Owen on the
              shoulder of I-45 was told he was at one, one line under the line
              that named the place he typed. A handover at a branch keeps its
              wording, which was already right. */}
          <div style={{ fontSize: 13, color: '#0F0F0F', fontWeight: 600 }}>
            {onTheRoad
              ? `Handing over on the road${toPlaceText.trim() ? ` at ${toPlaceText.trim()}` : ''} to ${branchNames[String(destId)] ? `${branchNames[String(destId)]}\u2019s driver` : 'the other driver'}`
              : toBranch
                ? `Handing over at ${branchNames[String(toBranch)] || 'a yard'} for ${branchNames[String(destId)] || 'them'} to collect`
                : `Driving straight to ${branchNames[String(destId)] || 'the destination'}`}
          </div>
          <div style={{ fontSize: 11, color: '#5C5C5C', marginTop: 3, lineHeight: 1.4 }}>
            {onTheRoad
              ? 'Handed to the other driver on the spot. It stays on this request and belongs to nobody\u2019s shelf until it reaches them.'
              : toBranch
                ? 'It stays on this request and belongs to nobody\u2019s shelf until it reaches them.'
                : 'Nobody hands this over on the way.'}
          </div>
          <details style={{ marginTop: 8 }}>
            <summary style={disclosureStyle(coarse)}>
              Change where it is handed over
            </summary>
            <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#3A3A3A', display: 'block', marginBottom: 5 }}>
              Where is this load being left?
            </label>
            <select
              value={toBranch}
              onChange={e => setToBranch(e.target.value)}
              style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, width: isMobile ? '100%' : 240, minHeight: TAP, boxSizing: 'border-box' }}
            >
              <option value="">Straight to {branchNames[String(destId)] || 'the destination'}</option>
              {Object.entries(branchNames)
                .filter(([id]) => String(id) !== String(myBranch) && String(id) !== String(destId))
                .map(([id, name]) => <option key={id} value={id}>Leave at {name}</option>)}
              {/* #553 D10's SCREEN HALF. The database has accepted a leg that
                  starts or ends at a place rather than a branch since 79.15f,
                  proven by constraint — and no control offered one, which is
                  #515's defect exactly: a capability nobody can reach reads as
                  finished. Colby: "a handoff should be allowed half way between
                  each location." A swap on I-45 is not a yard. */}
              <option value={TO_PLACE}>Handing over on the road…</option>
            </select>
            {toBranch === TO_PLACE && (
              <input
                value={toPlaceText}
                onChange={e => setToPlaceText(e.target.value)}
                placeholder="Where are you handing it over?"
                aria-label="Where are you handing it over?"
                style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, width: isMobile ? '100%' : 240, minHeight: TAP, boxSizing: 'border-box', marginTop: 8 }}
              />
            )}
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.4 }}>
              {/* #670 -- this said a swap on the road and a drop at a yard "are
                  the same thing here", against the ruling that a handover's text
                  is specific to it. They are two choices, offered as two. */}
              Pick where it changes hands — a branch yard, or a spot on the road — or send it
              straight through.
            </div>
            </div>
          </details>
        </div>
      )}

      {/* #553 D12 — A PICKER, NOT A TYPED NAME.
          It used to be a free-text "Driver" box, which recorded a string
          attached to no account and called it the driver. B6 is blunt about
          why that matters: a wrong name in an audit trail is worse than no
          name, and the stated purpose of the history is that "if anything
          happens the right person can be asked about the issue".

          IT IS STILL COLLAPSED AND STILL OPTIONAL. The ordinary Wednesday is
          two taps, and this is not one of them — the picker already defaults
          to whoever opened the sheet, so the common answer is right before
          anybody opens this. */}
      {/* #690, gate 665 D11 -- UNDER "LOADERS LOAD, DRIVERS DRIVE" NOBODY IS
          NAMED AT LOADING, on this sheet or on the To load card: the driver is
          named at the far end, when it is collected or counted in (553 D13).
          The setting was saved and changed nothing until now. The truck number
          stays, because it is a fact about the truck, not the driver. */}
      {loaderMode ? (
        <div>
          <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            Driver: named when it is collected or counted in
          </div>
          <details style={{ marginTop: 4 }}>
            <summary style={disclosureStyle(coarse)}>
              Truck number? <span style={{ color: 'var(--ink-3)' }}>optional</span>
            </summary>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <input
                value={truck} onChange={e => setTruck(e.target.value)} placeholder="Truck" aria-label="Truck"
                style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, flex: '1 1 120px', minHeight: TAP, boxSizing: 'border-box' }}
              />
            </div>
          </details>
        </div>
      ) : (
      <details>
        <summary style={disclosureStyle(coarse)}>
          Who is driving? <span style={{ color: 'var(--ink-3)' }}>
            {driverSummary}
          </span>
        </summary>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <select
            value={driverPick}
            onChange={e => setDriverPick(e.target.value)}
            aria-label="Who is driving?"
            style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, flex: '1 1 180px', minHeight: TAP, boxSizing: 'border-box', background: '#fff' }}
          >
            <option value="">Nobody recorded</option>
            {driverList.map(d => (
              <option key={d.id} value={d.id}>
                {d.isMe ? `${d.name} (me)` : d.name}
              </option>
            ))}
          </select>
          {driverPick === DRIVER_SOMEONE_ELSE && (
            <input
              value={driver}
              onChange={e => setDriver(e.target.value)}
              placeholder="Their name"
              aria-label="Their name"
              style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, flex: '1 1 150px', minHeight: TAP, boxSizing: 'border-box' }}
            />
          )}
          <input
            value={truck} onChange={e => setTruck(e.target.value)} placeholder="Truck"
            style={{ fontSize: 16, padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, flex: '1 1 120px', minHeight: TAP, boxSizing: 'border-box' }}
          />
        </div>
        {driverPick === DRIVER_SOMEONE_ELSE && (
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6, lineHeight: 1.4 }}>
            Recorded as signed by you on their behalf, so both names are on the load.
          </div>
        )}
      </details>
      )}

      {/* D54 — the value is stated PER LOAD, because since #515's D45 a made
          item travels at its cost to make and somebody can change that figure
          between the first truck and the second. */}
      {showValue && value && value.value != null && !nothingChosen && (
        <div style={{ fontSize: 12, color: '#3A3A3A' }}>
          Value leaving <strong>{money(value.value)}</strong>
          {value.anyUnpriced && <span style={{ color: '#8A5A10' }}> · some of this has no cost recorded</span>}
        </div>
      )}

      {mode !== 'unfilled' && (
        <div style={{ display: 'flex', gap: 8, flexDirection: isMobile ? 'column' : 'row' }}>
          <ActionButton
            onClick={() => onLoad({
              fromBranchId: myBranch,
              lines: payload,
              // D10 — a road spot and a branch are mutually exclusive, so the
              // branch id is dropped the moment a place is named.
              toBranchId: atOrigin && toBranch && toBranch !== TO_PLACE ? Number(toBranch) : null,
              toPlace: atOrigin && toBranch === TO_PLACE ? toPlaceText.trim() || null : null,
              // #690 -- loader mode names nobody, whatever the picker last held.
              ...(loaderMode ? { driverId: null, driverName: null } : driverPayload(driverPick, driver)),
              truck: truck.trim() || null,
            })}
            blockedReason={
              // #671 -- the load's own number and its limits come from what is
              // already on trucks; until that is read, Send waits and says so.
              !loadsReady ? 'Checking what is already on trucks…'
                // #705 -- "Only 1 of Frame WA 24x18 are standing here" read wrong.
                : over ? `Only ${over.__ceiling} of ${items[String(over.item_id)]?.name || 'that'} ${Number(over.__ceiling) === 1 ? 'is' : 'are'} standing here. Send that or less.`
                : nothingStanding ? nothingStandingMessage(transfer)
                : nothingChosen ? 'Type how many are going on this truck.'
                // D10. A place that is chosen and not named would be refused by
                // the database at the moment somebody pressed Send, with a
                // constraint name nobody can act on. #492: a control that
                // refuses a press answers it.
                : (atOrigin && toBranch === TO_PLACE && !toPlaceText.trim())
                  ? 'Say where you are handing it over.'
                : (!loaderMode && driverPick === DRIVER_SOMEONE_ELSE && !driver.trim())
                  ? 'Type the name of whoever is driving, or pick somebody from the list.'
                : null
            }
            busy={busy}
            busyLabel="Sending…"
            className="btn btn-primary"
            style={{ flex: 1, minHeight: COMMIT }}
          >
            Send {totalGoing > 0 ? totalGoing : ''}
          </ActionButton>

          {/* D56 — "Nothing more is coming" SITS BESIDE SEND rather than buried
              behind a menu, because Colby confirmed these are "a regular part of
              every day business". It is deliberately NOT the short close, which
              stays a report about goods that went missing. */}
          {onCloseUnfilled && progress.requested > progress.shipped && (
            <button
              onClick={() => setMode('unfilled')}
              className="btn"
              style={{ flex: isMobile ? undefined : '0 0 auto', minHeight: COMMIT, border: '1px solid #DDD', background: '#fff', color: '#5C5C5C' }}
            >
              Nothing more is coming
            </button>
          )}
        </div>
      )}

      {mode === 'unfilled' && (
        <NothingMoreComingForm
          owed={progress.requested - progress.shipped}
          destName={branchNames[String(destId)]}
          sourceName={branchNames[String(transfer.from_branch_id)]}
          reason={unfilledReason}
          onReason={setUnfilledReason}
          busy={busy}
          onConfirm={onCloseUnfilled}
          onCancel={() => setMode(null)}
        />
      )}
    </div>
  );
}

// #628 half 2, Session 79.22b -- ONE COPY OF THE WORDS, ON TWO TABS. "Nothing
// more is coming" lived on the load panel alone, and To load lists a transfer
// only when something is standing - so on a request nothing had been made
// for, the one thing a supplier can say was unreachable (acceptance line H5).
// Colby approved it on To make as well. The sentence and the reason box are
// shared so the two tabs cannot drift apart; each keeps its own open state.
export function NothingMoreComingForm({ owed, destName, sourceName = null, reason, onReason, busy, onConfirm, onCancel }) {
  const { coarse } = useTier();   // #727
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 12, color: '#5C5C5C', lineHeight: 1.45 }}>
        {/* NOTHING LEFT THE BUILDING, so there is no money to report. That
            is exactly why this is not the short close, and saying so here
            is what stops somebody reaching for the wrong one. */}
        This records that <strong>{owed}</strong> are never being sent.
        {/* #729 -- an admin or executive may say this from another branch, so
            the place is named rather than "here". */}
        Nothing moves and nothing is written off — none of it ever left {sourceName || 'here'}.
        {' '}{destName || 'The branch waiting'} stops expecting it.
      </div>
      <textarea
        value={reason}
        onChange={e => onReason(e.target.value)}
        rows={3}
        placeholder="Why not?"
        style={{ fontSize: 16, padding: 8, border: '1px solid #DDD', borderRadius: 8, width: '100%', boxSizing: 'border-box' }}
      />
      <ActionButton
        onClick={() => onConfirm(reason)}
        blockedReason={reason.trim() ? null : 'Say why — the person who asked reads this and it is all they get.'}
        busy={busy}
        busyLabel="Closing…"
        className="btn"
        style={{ minHeight: COMMIT, border: '1px solid #DDD', background: '#fff', color: '#3A3A3A' }}
      >
        Nothing more is coming
      </ActionButton>
      <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 13, color: '#5C5C5C', cursor: 'pointer', minHeight: controlHeight(coarse, 'min') }}>Cancel</button>
    </div>
  );
}

// #628 half 2 -- the To make sheet's own "Nothing more is coming": a button,
// and the shared form once it is pressed. Module level, never inside the
// sheet (locked rule 2).
export function NothingMoreComingOnMake({ transfer, lines, branchNames = {}, busy, isMobile, onCloseUnfilled }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const progress = transferProgress(lines);
  // No callback means this person may not say it - the caller asks
  // canSayNothingMoreComing() and passes null, exactly as for the load panel.
  if (!onCloseUnfilled) return null;
  if (progress.requested <= progress.shipped) return null;
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn"
        style={{ alignSelf: isMobile ? 'stretch' : 'flex-start', minHeight: COMMIT, border: '1px solid #DDD', background: '#fff', color: '#5C5C5C' }}>
        Nothing more is coming
      </button>
    );
  }
  return (
    <NothingMoreComingForm
      owed={progress.requested - progress.shipped}
      destName={branchNames[String(transfer.to_branch_id)]}
      sourceName={branchNames[String(transfer.from_branch_id)]}
      reason={reason}
      onReason={setReason}
      busy={busy}
      onConfirm={onCloseUnfilled}
      onCancel={() => { setOpen(false); setReason(''); }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCREENS 2 AND 3 — A LOAD TURNS UP, AND IS COUNTED
// ─────────────────────────────────────────────────────────────────────────────
export function ArrivalPanel({
  transfer, loads, items, branchNames, myBranch, isMobile, busy, onArrive,
  onMarkArrived,
}) {
  const [openId, setOpenId] = useState(null);
  const [counting, setCounting] = useState(false);
  const [counts, setCounts] = useState({});
  const [note, setNote] = useState('');
  const { coarse } = useTier();   // #727

  // Only loads heading HERE. Somebody standing at Dallas cannot count a load
  // that is on its way to Austin, and offering it is #492's class exactly —
  // a control that accepts a press and cannot work.
  //
  // ** A LOAD ALREADY MARKED AS HERE STILL BELONGS ON THIS LIST — #553 D17. **
  // This asked only for `in_transit`, which was right while pressing arrive WAS
  // the count. Now the driver taps on pulling in and somebody else counts it
  // the next morning, so between those two events sits a truck in the yard with
  // nothing counted — and keying this on `in_transit` would have dropped it off
  // the very screen the morning crew works from.
  //
  // WHAT TAKES A ROW OFF THIS LIST IS THE COUNT, never the arrival.
  const here = (loads || []).filter(
    l => (l.status === 'in_transit' || (l.status === 'arrived' && !l.counted_at)) &&
      String(l.to_branch_id) === String(myBranch),
  );

  // The truck is in the yard and nobody has opened the boxes. Worth saying out
  // loud on the row, because "arrived" used to mean counted and a reader who
  // learned the old meaning will otherwise assume the stock has landed.
  const awaiting = here.filter(l => l.status === 'arrived' && !l.counted_at);
  // #729, Session 79.22b.6e -- the reader's own branch, by name: every load on
  // this list is heading there, and "here" said nothing to an admin under All
  // branches. Null only while names load, when the old words stand.
  const homeName = branchNames[String(myBranch)] || null;

  // NOTHING HEADING HERE IS USUALLY CORRECT -- you are at the sending branch,
  // or everything has already been counted in. But there is one shape that is
  // NOT correct and must not render as a blank space: the transfer says it is
  // on its way, you are standing at the destination, and there is no load.
  //
  // That means the goods were sent by the one-shot path this build replaced,
  // which recorded a quantity and no movement. Saying so is #492's standard --
  // a screen that cannot offer an action says why -- and it makes the state
  // diagnosable instead of looking like the app forgot how to receive.
  //
  // ** AND `loads.length === 0` IS LOAD-BEARING, NOT A TIGHTENING. ** Without
  // it this fired on a load created the same morning: material waiting at
  // Austin is not arriving HERE, so `here` is empty and the panel announced
  // that the data was broken. It was not -- the frames were at a yard, and the
  // collect panel directly above was already saying so. A message telling
  // somebody to fetch a manager for a healthy transfer is worse than silence.
  // Legacy data means NO load record exists at all, which is what this now asks.
  const strandedHere =
    transfer?.status === 'in_transit' &&
    String(transfer?.to_branch_id) === String(myBranch) &&
    (loads || []).length === 0;

  if (here.length === 0) {
    if (!strandedHere) return null;
    return (
      <div style={{
        background: '#FDF3E3', borderLeft: '3px solid #EF9F27', borderRadius: 8,
        padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
      }}>
        <strong>This was sent before Stede recorded loads,</strong> so there is no load to
        count in. The quantity is on the request but the truck it travelled on was never
        written down. A manager needs to put this right before it can be booked in.
      </div>
    );
  }

  const open = here.find(l => String(l.id) === String(openId)) || (here.length === 1 ? here[0] : null);

  const rows = open ? (open.branch_transfer_load_items || []) : [];
  const countRows = rows.map(r => ({
    item_id: r.item_id,
    counted: counting ? (counts[String(r.item_id)] ?? '') : '',
  }));

  // Previewed from what is typed rather than computed after the fact, so the
  // person sees the gap before they commit it.
  const preview = discrepancy(rows.map(r => ({
    ...r,
    qty_arrived: counting && counts[String(r.item_id)] !== '' && counts[String(r.item_id)] != null
      ? Number(counts[String(r.item_id)])
      : r.qty_loaded,
  })));
  // #587 -- at the destination a count above what was sent is caught HERE, in
  // plain words, rather than refused by the database after the press. A yard
  // keeps D63's "extra" wording, because there nothing is booked.
  const overCount = counting && open
    ? overCountRefusal({ load: open, transfer, preview, branchNames })
    : null;
  // #682, Session 79.22b.6c.2b -- P4: where the sending branch can put the truck
  // right, a count above it goes ahead, and Count in says first what it will do
  // with the extra (682 D7, callout I). At a stop, and on a truck from one or a
  // hub's collection, overCountRefusal() above refuses it instead.
  const overAsk = counting && open && !overCount
    ? overCountAsk({ load: open, transfer, preview, branchNames, items })
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* THE HEADING MUST NOT SAY "on its way" ABOUT A TRUCK IN THE YARD.
          #553 D17 put two different states on this list, and 79.14's lesson was
          a band that repeated two of its three facts and omitted the third —
          a header that lies about what you are looking at is #544's class. */}
      <div style={{ fontSize: 13, fontWeight: 700, color: '#3A3A3A' }}>
        {/* #729 -- the branch is named: an admin under All branches has no
            "here". These loads are the reader's own branch's (`here` above). */}
        {countInHeading({ count: here.length, awaiting: awaiting.length, branch: homeName })}
      </div>

      {/* D55 — DALLAS RECEIVES A LOAD, NOT A TRANSFER. With two trucks on the
          road a screen offering one box per item cannot tell which load the
          person is standing in front of. */}
      {here.length > 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {here.map(l => {
            const on = open && String(l.id) === String(open.id);
            const total = (l.branch_transfer_load_items || [])
              .reduce((n, li) => n + (Number(li.qty_loaded) || 0), 0);
            return (
              <button
                key={l.id}
                onClick={() => { setOpenId(l.id); setCounting(false); setCounts({}); }}
                style={{
                  textAlign: 'left', cursor: 'pointer', minHeight: TAP,
                  border: on ? '1.5px solid #1D9E75' : '1px solid #E0E0E0',
                  background: on ? '#F1FAF6' : '#fff',
                  borderRadius: 8, padding: '10px 12px',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: on ? 700 : 600, color: '#0F0F0F' }}>
                  {l.load_ref} · {total} item{total === 1 ? '' : 's'}
                </div>
                <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>
                  Left {l.from_place || branchNames[String(l.from_branch_id)] || 'elsewhere'}
                  {l.loaded_at ? ` ${new Date(l.loaded_at).toLocaleDateString()}` : ''}
                  {l.driver_name ? ` · ${l.driver_name}` : ''}
                </div>
                {/* WHICH OF THE TWO STATES THIS ROW IS IN, on the row itself.
                    With one truck in the yard and another still on the road,
                    the chooser is the only place the difference is visible. */}
                {l.status === 'arrived' && !l.counted_at && (
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: '#1D6F52',
                    background: '#E8F6F0', borderRadius: 999,
                    padding: '2px 8px', marginTop: 6, display: 'inline-block',
                  }}>
                    {homeName ? `At ${homeName} since` : 'Here since'} {l.arrived_at ? new Date(l.arrived_at).toLocaleDateString() : 'earlier'} · not counted
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {open && (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 0 /* #710 */ }}>
            <tbody>
              {rows.map(r => {
                const name = r.inventory_items?.name || items[String(r.item_id)]?.name || `Item ${r.item_id}`;
                return (
                  <tr key={r.id || r.item_id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                    <td style={{ padding: '9px 0', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: 14, color: '#0F0F0F', fontWeight: 600 }}>{name}</div>
                      <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>
                        {r.qty_loaded} sent on {open.load_ref}
                      </div>
                    </td>
                    {counting && (
                      <td style={{ padding: '9px 0 9px 12px', textAlign: 'right', width: 116 }}>
                        <div style={QTY_LABEL}>Counted</div>
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          value={counts[String(r.item_id)] ?? ''}
                          placeholder={String(r.qty_loaded)}
                          onChange={e => setCounts(c => ({ ...c, [String(r.item_id)]: e.target.value }))}
                          className={QTY_BOX_CLASS}
                          style={qtyBox()}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* D63 — THE DEFAULT IS SILENCE. The screen assumes what was sent is
              what is there, so the ordinary Wednesday is two taps. Saying
              something is wrong is ONE EXTRA TAP, taken only when it is. */}
          {!counting && (
            <button
              onClick={() => setCounting(true)}
              style={{
                background: 'none', border: 'none', padding: 0, textAlign: 'left',
                fontSize: 13, color: '#8A5A10', cursor: 'pointer', minHeight: controlHeight(coarse, 'min'), textDecoration: 'underline',
              }}
            >
              Something is missing or there is extra
            </button>
          )}

          {counting && !preview.clean && !(overAsk && !(preview.missing > 0)) && (
            <div style={{
              background: '#FDF3E3', borderLeft: '3px solid #EF9F27', borderRadius: 8,
              padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
            }}>
              {preview.missing > 0 && <div><strong>{preview.missing} missing</strong> against what was sent.</div>}
              {preview.extra > 0 && !overAsk && <div><strong>{preview.extra} extra</strong> against what was sent.</div>}
              <div style={{ marginTop: 4 }}>
                {overCount
                  ? overCount.notice
                  : `Recorded against ${open.load_ref}, where it happened. Only what you counted is booked in.`}
              </div>
            </div>
          )}

          {/* #682 -- P4's notice, callout I, as drawn: the attention pair, 13px,
              under the count it answers. */}
          {overAsk && (
            <p role="status" style={{
              margin: 0, background: '#FFF8EC', color: '#8A5A08', borderRadius: 8,
              padding: '8px 12px', fontSize: 13, lineHeight: 1.35,
            }}>
              {overAsk.notice}
            </p>
          )}

          {counting && (
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              placeholder="What was wrong? (optional)"
              style={{ fontSize: 16, padding: 8, border: '1px solid #DDD', borderRadius: 8, width: '100%', boxSizing: 'border-box' }}
            />
          )}

          {/* ** TWO BUTTONS FOR TWO EVENTS — #553 D17. **

              This was ONE button reading "<ref> is here", and that label was
              the defect at the control: pressing it landed the stock, so
              saying the truck had arrived and booking its contents onto the
              shelf were the same act. They are not. A truck back at 8:40pm is
              here; nothing has been counted; nothing should be on a shelf.

              THE COUNT IS THE PRIMARY ACTION because it is the one that
              finishes the job, and it works from either state — the database
              fills the arrival time in if the tap never happened.

              THE TAP IS OFFERED ONLY WHILE THE LOAD IS STILL ON THE ROAD.
              Once it is marked here the button would do nothing, and a control
              that accepts a press and does nothing is #492 exactly. */}
          <div style={{
            display: 'flex', gap: 10, flexWrap: 'wrap',
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <ActionButton
              onClick={() => onArrive({ load: open, counts: countRows, note: note.trim() || null })}
              blockedReason={overCount ? overCount.blockedReason : null}
              busy={busy}
              busyLabel="Booking in…"
              className="btn btn-primary"
              style={{ minHeight: COMMIT, width: isMobile ? '100%' : 220 }}
            >
              {/* #682 -- P4: above the truck, "The button keeps its name" (callout I). */}
              {overAsk ? 'Count in' : counting && !preview.clean ? 'Book in what arrived' : `Count in ${open.load_ref}`}
            </ActionButton>

            {open.status === 'in_transit' && onMarkArrived && (
              <ActionButton
                onClick={() => onMarkArrived({ load: open })}
                busy={busy}
                busyLabel="Saving…"
                className="btn"
                style={{
                  minHeight: COMMIT, width: isMobile ? '100%' : 'auto',
                  border: '1px solid #1D9E75', background: '#fff', color: '#1D6F52',
                  fontWeight: 600,
                }}
              >
                It is here — count it later
              </ActionButton>
            )}
          </div>

          {open.status === 'arrived' && !open.counted_at && (
            <div style={{ fontSize: 12, color: '#5C5C5C', lineHeight: 1.45 }}>
              {homeName ? `Marked as arrived at ${homeName}` : 'Marked as here'}
              {open.arrived_at ? ` on ${new Date(open.arrived_at).toLocaleDateString()}` : ''}.
              <strong> Nothing is on the shelf until somebody counts it in.</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// THE HANDOVER — COLLECTING FROM A HUB OR A ROADSIDE SWAP
//
// #529, Session 79.10. Colby: "dropping off frames at the hub in austin is the
// standard... a handoff happens 99.5% of the time", and "usually no count, but
// the driver can."
//
// ** THIS SCREEN DID NOT EXIST AND ITS TAB DID. ** "To collect" listed the
// pallet at Austin and offered nothing to do with it, then showed a message
// about data predating loads — false for a load created that morning. The door
// was built and the room was not.
//
// ONE PRESS. The driver taps the card and taps Collected. Behind it the inbound
// leg ends and the onward leg starts, because those are two records and one
// act. Making somebody press twice would put the normal path at four taps while
// the rare straight-through run costs two.
//
// A SWAP AND A HUB DROP ARE THE SAME RECORD — his ruling. A stretch of road and
// a yard differ only in where the material waited.
// ─────────────────────────────────────────────────────────────────────────────
export function CollectPanel({
  transfer, loads, items, branchNames, myBranch, isMobile, busy, onCollect,
  auth, drivers = [],
}) {
  const { coarse } = useTier();   // #672
  // #629 -- which stop is open, by its KEY: a branch or a place on the road.
  // A road stop has no branch id, so selecting by branch could never pick it.
  const [openStop, setOpenStop] = useState(null);
  const [counting, setCounting] = useState(false);
  const [found, setFound] = useState({});   // what was actually at the yard
  const [take, setTake] = useState({});     // what goes on MY truck today
  const [note, setNote] = useState('');

  // #553 D11, Session 79.21b -- ONE SIGN-OFF, TWO NAMES ON THE RECORD. The
  // handing driver's declared quantity is already on the inbound leg; the
  // person meeting the truck counts and signs, and the leg records who took
  // custody. This screen passed nobody, so `hand_over_load()` wrote the leg as
  // "dropped" with no receiving name -- material changing hands with one name
  // on it, which is the thing D11 exists to prevent. Prefilled with whoever
  // opened the panel, like the load sheet's picker: the common answer costs
  // no taps.
  const takerList = useMemo(
    () => driverChoices(drivers, auth?.user?.id),
    [drivers, auth?.user?.id],
  );
  const [takerPick, setTakerPick] = useState('');
  const [takerName, setTakerName] = useState('');
  // #699, gate 665 D28 -- presserDriver(), the one rule the card uses too.
  useEffect(() => {
    const me = presserDriver(drivers, auth?.user?.id);
    if (me) setTakerPick(prev => prev || me);
  }, [auth?.user?.id, drivers]);

  // Only the branch the request is FOR goes and gets it. Houston looking at
  // this transfer has no business loading Dallas's truck.
  const isMine = String(transfer?.to_branch_id) === String(myBranch);
  const stops = useMemo(
    () => (isMine ? hubsWaitingFor(loads, transfer) : []),
    [isMine, loads, transfer]);

  if (stops.length === 0) return null;

  const stop = stops.find(x => stopKey(x) === openStop)
    || (stops.length === 1 ? stops[0] : null);
  const where = stop ? (stop.place || branchNames[String(stop.branchId)] || 'the handover') : '';
  const home = branchNames[String(transfer?.to_branch_id)] || 'here';

  // COUNTING ONLY WHERE A COUNT HAS AN HONEST HOME -- exactly one truck being
  // met. With two loads at one yard, "190 were here" cannot be attributed to
  // either without inventing a split.
  const canCount = !!stop && stop.loads.length === 1;

  // What is actually available on a row, once any count has been applied. A
  // count corrects what is ON THE TRUCK being met; it cannot change what was
  // already standing on the ground.
  const availableOn = (r) => {
    if (!counting) return r.available;
    const typed = found[String(r.item_id)];
    const rolling = typed === '' || typed == null ? r.rolling : Number(typed);
    return r.standing + (Number.isFinite(rolling) && rolling >= 0 ? rolling : r.rolling);
  };
  const takeOn = (r) => {
    const typed = take[String(r.item_id)];
    const avail = availableOn(r);
    if (typed === '' || typed == null) return avail;   // default: all of it
    const n = Number(typed);
    return Number.isFinite(n) && n >= 0 ? Math.min(n, avail) : avail;
  };

  const rows = stop ? stop.rows : [];
  const taking = rows.map(r => ({
    item_id: r.item_id, qty: takeOn(r), unit_cost: r.unit_cost, sourced: r.sourced,
  }));
  const totalTaking = taking.reduce((n, l) => n + l.qty, 0);
  const totalAvail = rows.reduce((n, r) => n + availableOn(r), 0);
  const leaving = Math.max(totalAvail - totalTaking, 0);
  const overTyped = rows.some(r => {
    const typed = take[String(r.item_id)];
    return typed !== '' && typed != null && Number(typed) > availableOn(r);
  });

  // #553 D11 -- A DIFFERENCE AT THE SWAP RAISES A DISCREPANCY, and a
  // discrepancy with no words is a number two drivers will argue about later.
  // Only a SHORTFALL against what was handed over is asked about: less arrived
  // than the other driver declared.
  const shortAtSwap = counting
    ? rows.reduce((n, r) => {
      const typed = found[String(r.item_id)];
      if (typed === '' || typed == null) return n;
      const c = Number(typed);
      return Number.isFinite(c) && c < r.rolling ? n + (r.rolling - c) : n;
    }, 0)
    : 0;
  // #682, Session 79.22b.6c.2b (682 D8, at its second writer) -- AND NOTHING
  // ABOVE WHAT THE TRUCK CARRIED. A collection's count is the arrived number of
  // the one truck it closes, and hand_over_load() now refuses one above that
  // truck in the words Count in uses at a stop; so the panel says them before
  // the press, from the same check Count in uses, on the same truck.
  const metTruck = counting && canCount ? stop.loads[0] : null;
  const overAtSwap = metTruck
    ? overCountRefusal({
      load: metTruck, transfer, branchNames,
      preview: discrepancy((metTruck.branch_transfer_load_items || []).map(li => {
        const typed = found[String(li.item_id)];
        const c = typed === '' || typed == null ? NaN : Number(typed);
        return { ...li, qty_arrived: Number.isFinite(c) && c >= 0 ? c : li.qty_loaded };
      })),
    })
    : null;
  const takerMissing = takerPick === DRIVER_SOMEONE_ELSE && !takerName.trim();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#3A3A3A' }}>
        {stops.length === 1
          ? `Waiting at ${where}`
          : `Waiting at ${stops.length} places`}
      </div>

      {stops.length > 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {stops.map(x => {
            const on = stop && stopKey(x) === stopKey(stop);
            return (
              <button key={stopKey(x)}
                onClick={() => { setOpenStop(stopKey(x)); setCounting(false); setFound({}); setTake({}); }}
                style={{
                  textAlign: 'left', cursor: 'pointer', minHeight: TAP,
                  border: on ? '1.5px solid #378ADD' : '1px solid #E0E0E0',
                  background: on ? '#EEF5FC' : '#fff', borderRadius: 8, padding: '10px 12px',
                }}>
                <div style={{ fontSize: 14, fontWeight: on ? 700 : 600, color: '#0F0F0F' }}>
                  {x.place || branchNames[String(x.branchId)] || `Branch ${x.branchId}`} · {x.total}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {stop && (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 0 /* #710 */ }}>
            <tbody>
              {rows.map(r => {
                const k = String(r.item_id);
                const avail = availableOn(r);
                const mine = takeOn(r);
                const left = Math.max(avail - mine, 0);
                return (
                  <tr key={k} style={{ borderBottom: '1px solid #F0F0F0' }}>
                    <td style={{ padding: '9px 0', verticalAlign: 'top' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0F0F0F' }}>
                        {r.name || items[k]?.name || `Item ${r.item_id}`}
                      </div>
                      <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>
                        {avail} at {where}
                        {r.rolling > 0 && r.standing > 0
                          ? ` · ${r.standing} on the ground, ${r.rolling} arriving`
                          : r.rolling > 0 ? ' · arriving' : ''}
                      </div>
                      {/* ** THE ANSWER TO "HOW DO I PICK THEM UP ANOTHER DAY" **
                          -- it stays exactly where it is and keeps appearing on
                          To collect until somebody fetches it. */}
                      {left > 0 && (
                        <div style={{ fontSize: 12, color: '#8A5A10', marginTop: 3, fontWeight: 600 }}>
                          {left} stays at {where}
                        </div>
                      )}
                    </td>
                    {counting && (
                      <td style={{ padding: '9px 0 9px 10px', textAlign: 'right', width: 92, verticalAlign: 'top' }}>
                        <div style={QTY_LABEL}>Here</div>
                        <input
                          type="number" inputMode="decimal" min="0"
                          value={found[k] ?? ''}
                          placeholder={String(r.rolling)}
                          onChange={e => setFound(c => ({ ...c, [k]: e.target.value }))}
                          className={QTY_BOX_CLASS}
                          style={qtyBox({ width: 92 })}
                        />
                      </td>
                    )}
                    <td style={{ padding: '9px 0 9px 10px', textAlign: 'right', width: 100, verticalAlign: 'top' }}>
                      <div style={QTY_LABEL}>Taking</div>
                      <input
                        type="number" inputMode="decimal" min="0" max={avail}
                        value={take[k] ?? String(avail)}
                        onChange={e => setTake(c => ({ ...c, [k]: e.target.value }))}
                        className={QTY_BOX_CLASS}
                        style={qtyBox({ width: 92 })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* #492 -- it says why rather than going quiet. */}
          {overTyped && (
            <div style={{ fontSize: 13, color: '#A33', fontWeight: 600 }}>
              There is only so much at {where}. Anything above what is there has been
              brought back down.
            </div>
          )}

          {/* COUNTING IS THE EXCEPTION TAP -- Colby: "usually no count, but the
              driver can." A sealed trailer changing hands has nothing to count. */}
          {canCount && !counting && (
            <button onClick={() => setCounting(true)}
              style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left',
                fontSize: 13, color: '#8A5A10', cursor: 'pointer', minHeight: controlHeight(coarse, 'min'), textDecoration: 'underline' }}>
              Count what is here first
            </button>
          )}

          {counting && (
            <>
              {/* #682 -- a count above the truck, refused before the press in
                  Count in's own words at a stop (682 D8). */}
              {overAtSwap && (
                <div role="status" style={{
                  background: '#FDF3E3', borderLeft: '3px solid #EF9F27', borderRadius: 8,
                  padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
                }}>
                  {overAtSwap.notice}
                </div>
              )}
              {shortAtSwap > 0 && (
                <div style={{
                  background: '#FDF3E3', borderLeft: '3px solid #EF9F27', borderRadius: 8,
                  padding: '10px 12px', fontSize: 13, color: '#8A5A10', lineHeight: 1.45,
                }}>
                  {/* #631 amendment, 79.22b -- gate 553 F2's approved words, which
                      acceptance line F5 quotes. The old sentence was never the
                      approved one and could not be checked until #629 made a
                      road handover reachable. */}
                  <strong>{shortAtSwap} short.</strong> This is recorded against both of you with the
                  time and the place. Say what happened before you take it on.
                </div>
              )}
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder={shortAtSwap > 0 ? 'What was wrong?' : 'What was wrong? (optional)'}
                style={{ padding: 8, border: '1px solid #DDD', borderRadius: 8, width: '100%', boxSizing: 'border-box' }} />
            </>
          )}

          {/* #553 D11 -- WHO IS TAKING IT. Collapsed and prefilled, like the
              load sheet's driver picker, so the ordinary swap costs no taps and
              the leg still carries a name. */}
          <details>
            <summary style={disclosureStyle(coarse)}>
              Who is taking it? <span style={{ color: 'var(--ink-3)' }}>
                {takerPick === DRIVER_SOMEONE_ELSE
                  ? (takerName.trim() || 'somebody else')
                  : (takerList.find(d => d.id === takerPick)?.name || 'nobody recorded')}
              </span>
            </summary>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <select
                value={takerPick}
                onChange={e => setTakerPick(e.target.value)}
                aria-label="Who is taking it?"
                style={{ padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, flex: '1 1 180px', minHeight: TAP, boxSizing: 'border-box', background: '#fff' }}
              >
                <option value="">Nobody recorded</option>
                {takerList.map(d => (
                  <option key={d.id} value={d.id}>{d.isMe ? `${d.name} (me)` : d.name}</option>
                ))}
              </select>
              {takerPick === DRIVER_SOMEONE_ELSE && (
                <input
                  value={takerName}
                  onChange={e => setTakerName(e.target.value)}
                  placeholder="Their name"
                  aria-label="Their name"
                  style={{ padding: '9px 10px', border: '1px solid #DDD', borderRadius: 8, flex: '1 1 150px', minHeight: TAP, boxSizing: 'border-box' }}
                />
              )}
            </div>
          </details>

          <ActionButton
            onClick={() => onCollect({
              stop,
              taking: taking.filter(l => l.qty > 0),
              counts: counting
                ? rows.map(r => ({ item_id: r.item_id, counted: found[String(r.item_id)] ?? '' }))
                : null,
              note: note.trim() || null,
              leaving,
              // D11 -- who took custody, in the shape the column constraint
              // allows: an account or a typed name, never both.
              ...(({ driverId, driverName }) => ({ toUser: driverId, toName: driverName }))(
                driverPayload(takerPick, takerName)),
            })}
            blockedReason={
              overAtSwap ? overAtSwap.blockedReason
                : shortAtSwap > 0 && !note.trim()
                  ? 'Say what happened before you take it on.'
                  : takerMissing ? 'Type the name of whoever is taking it.'
                    : null
            }
            busy={busy} busyLabel="Recording…"
            className="btn btn-primary"
            style={{ minHeight: COMMIT, width: isMobile ? '100%' : 300, background: '#378ADD', borderColor: '#378ADD' }}
          >
            {totalTaking === 0
              ? `Take nothing from ${where}`
              : leaving > 0
                ? `Take ${totalTaking}, leave ${leaving}`
                : `Collected from ${where}`}
          </ActionButton>

          <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.4 }}>
            {leaving > 0
              // #705 -- right for one and for many: "5 goes on your truck" and
              // "the other 3 stays" read wrong for every pile but one.
              ? `${totalTaking} ${totalTaking === 1 ? 'goes' : 'go'} on your truck to ${home}. The other ${leaving} ${leaving === 1 ? 'stays' : 'stay'} at ${where} and ${leaving === 1 ? 'keeps' : 'keep'} showing here until somebody fetches ${leaving === 1 ? 'it' : 'them'}.`
              : stop.loads.length > 0
                ? `This closes the leg into ${where} and starts yours to ${home} in one go. Count it in when you arrive.`
                /* Nothing is inbound -- it is already on the ground, so there is
                   no leg to close and claiming otherwise is just untrue. */
                : `This puts the last of it on your truck to ${home}. Count it in when you arrive.`}
          </div>
        </>
      )}
    </div>
  );
}

// A small read-only summary of every load on a transfer, for whoever is just
// looking. It answers "where is my stuff" without anybody having to open a
// second screen.
// ── #682 / #702, Session 79.22b.6c.2 -- CORRECT ON A HISTORY LINE ──────────
//
// WHO SEES IT: whoever canCorrectStep() lets in for at least one of the line's
// items -- the person who recorded its current number, or a branch lead there
// (682 D3, D15). Everybody else sees the history with no button at all (F1),
// the way an Undo is shown only to its presser. WHETHER the line can be
// corrected at all is decided by transferHistory() from the records.
//
// THE PIECES ARE MODULE-LEVEL, never defined inside LoadHistory (locked rule 2).
export function correctableFor(auth, e) {
  const c = e && e.correct;
  if (!c || !Array.isArray(c.items)) return null;
  const mine = c.items.filter(it => canCorrectStep(auth, { kind: c.kind, branchId: c.branchId, recordedBy: it.recordedBy }));
  if (!mine.length) return null;
  // One item that is waiting on the other branch answers with the next step
  // instead (682 D5, F2); a finished transfer answers for the whole line (S2).
  const blocked = c.blocked || (mine.length === 1 ? mine[0].blocked : null) || null;
  return { kind: c.kind, context: c, items: mine, blocked };
}

// The frames' button (gk-p .btn): Barlow Condensed, capitals, white with a hair
// edge. 48px full width under the line on a phone (P1); on a desk, 32px with a
// mouse and the 40px floor with a finger, in a narrow column at the row's right
// (P7, callout N).
const CORRECT_BTN = {
  margin: 0, borderRadius: 8, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
  letterSpacing: '.5px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center',
  lineHeight: 1, background: '#fff', color: '#0F0F0F', border: '1px solid #D0D0D0', whiteSpace: 'nowrap', cursor: 'pointer',
};
// S2's notice (gk-s .notice): the attention pair, under the button.
const CORRECT_NOTICE = {
  margin: '12px 0 0', background: '#FFF8EC', color: '#8A5A08', borderRadius: 8, padding: '8px 12px',
  fontSize: 13, lineHeight: 1.35,
};

// A BLOCKED CORRECT IS KEPT AND ANSWERS (#492, callout Q). It looks as drawn --
// the ordinary button, its reason beneath -- and carries ActionButton's
// semantics: aria-disabled, the reason linked to it, announced when pressed.
export function CorrectButton({ id, desk = false, coarse = false, blocked = null, onPress, label = 'Correct' }) {
  const [said, setSaid] = useState(false);
  const reasonId = `${id}-why`;
  const size = desk
    ? { height: coarse ? controlHeight(true, 'min') : controlHeight(false, 'commit'), fontSize: 13, padding: '0 10px' }
    : { height: 48, fontSize: 14, padding: '0 12px', width: '100%', marginTop: 8 };
  return (
    <>
      <button
        type="button"
        aria-disabled={blocked ? true : undefined}
        aria-describedby={blocked && !desk ? reasonId : undefined}
        aria-label={blocked && desk ? `${label}. ${blocked}` : undefined}
        onClick={() => { if (blocked) { setSaid(true); return; } onPress?.(); }}
        style={{ ...CORRECT_BTN, ...size }}
      >{label}</button>
      {blocked && !desk && (
        <p id={reasonId} role={said ? 'alert' : undefined} style={CORRECT_NOTICE}>{blocked}</p>
      )}
    </>
  );
}

// THE CORRECTED LINE'S WORDS (682 D10, F2): the old number struck, the new one
// beside it. Houston's Yes reads "confirmed by" with the count that asked.
function Struck({ was, now }) {
  return <><s>{qtyText(was)}</s> {qtyText(now)}</>;
}

export function CorrectedLine({ c, at = null, refText = null }) {
  const when = shortWhen(at);
  const tail = c.confirmed
    ? [`confirmed by ${c.by || 'the sending branch'}`, `${c.confirmed.place} counted ${qtyText(c.confirmed.counted)}`]
    : [`corrected by ${c.by || 'not recorded'}`, c.reason];
  return (
    <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: '#0F0F0F' }}>
      {c.label} <Struck was={c.was} now={c.now} />{c.item ? ` ${c.item}` : ''}
      {[...tail, when, refText].filter(Boolean).map((t, i) => <span key={i}>{` · ${t}`}</span>)}
    </div>
  );
}

// A several-item truck: one line per corrected item beneath its total (F2).
export function CorrectedSubLines({ lines = [], desk = false }) {
  if (!lines.length) return null;
  return lines.map(s => (
    <span key={s.key} style={{ display: 'block', fontWeight: 400, fontSize: desk ? 11 : 13, color: '#5C5C5C', marginTop: 2 }}>
      {s.item} <Struck was={s.was} now={s.now} />
      {(s.confirmed
        ? [`confirmed by ${s.by || 'the sending branch'}`, `${s.confirmed.place} counted ${qtyText(s.confirmed.counted)}`]
        : [`corrected by ${s.by || 'not recorded'}`, s.reason]).filter(Boolean).map((t, i) => <span key={i}>{` · ${t}`}</span>)}
    </span>
  ));
}

// #682, Session 79.22b.6c.2b -- S5's Alert, as drawn: the attention pair, 13px,
// with its kicker in the head face above the sentence (the gate's .notice and
// .kicker). A pure drawing: it holds no control, so it has no height to size.
function AlertNotice({ text }) {
  return (
    <p role="note" style={{
      margin: 0, background: '#FFF8EC', color: '#8A5A08', borderRadius: 8,
      padding: '8px 12px', fontSize: 13, fontWeight: 400, lineHeight: 1.35,
    }}>
      <span style={{
        display: 'block', margin: '0 0 4px', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 12,
        letterSpacing: '.5px', textTransform: 'uppercase', color: 'inherit',
      }}>Alert</span>
      {text}
    </p>
  );
}

export function LoadHistory({ loads, branchNames, items, transfer = null, usersById = {}, production = [], handovers = [], countIns = [], corrections = [], extras = [], namesLoading = false, isMobile = false, auth = null, onCorrect = null }) {
  const [full, setFull] = useState(false);
  // #581 -- 44px with a finger, 32 with a mouse (#417 D2's floors).
  const { coarse, width } = useTier();
  // #682 / #702 -- ON A DESK, CORRECT SITS AT THE RIGHT OF THE ROW (P7, callout
  // N), even where the desk's right-hand column stacks the history; full width
  // under the line is the phone's and the tablet's (P1, F4).
  const deskWindow = tierAtLeast(tierFor(width), 'desk');

  // `items` names the refused items on a partly refused transfer's
  // confirmation line (#573). `handovers` (#694, 79.22b.4.6.1b) keeps a
  // collection that was taken back in the journey, and `countIns`
  // (79.22b.4.6.2) a count that was. `corrections` and `extras` (#682 /
  // #702, 79.22b.6c.2) draw a corrected step as one line and say which lines
  // can be corrected.
  const events = useMemo(
    () => transferHistory(loads, { branchNames, usersById, transfer, items, production, handovers, countIns, corrections, extras }),
    [loads, branchNames, usersById, transfer, items, production, handovers, countIns, corrections, extras],
  );
  // A truck's corrected line names its truck only when there is more than one
  // to tell apart -- the way a sign-off names its item only when there are
  // several (the gate's "{ item, when several}").
  const manyTrucks = (Array.isArray(loads) ? loads : []).length > 1;
  const fixOf = (e) => (onCorrect && auth ? correctableFor(auth, e) : null);

  // H13 — computed BEFORE the early return, because a transfer can legitimately
  // have material standing at a hub while its history is empty, and returning
  // null first would hide the one line that says where the material is.
  const standingLines = useMemo(() => {
    if (!transfer) return [];
    return hubsWaitingFor(loads, transfer)
      .map(stop => ({ key: `standing:${stopKey(stop)}`, text: standingSentence(stop, branchNames, items) }))
      .filter(line => line.text);
  }, [loads, transfer, branchNames, items]);

  if (events.length === 0 && standingLines.length === 0) return null;

  // H15 — CONDENSED BY DEFAULT, the full list behind a link. A transfer that
  // went through a yard twice has a dozen events and the question a person
  // opens this for is almost always "where is it now".
  const CONDENSED = 3;
  const hidden = Math.max(events.length - CONDENSED, 0);
  const shown = full ? events : events.slice(-CONDENSED);

  const when = (d) => d
    ? d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })
      + ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : '—';

  // #576, ruled by Colby at Session 79.21: an approval whose time was never
  // recorded SAYS so, in the same muted words the WHO column already uses,
  // rather than printing the time it was backfilled from or a bare dash that
  // reads like a missing value.
  const whenCell = (e) => (e.timeNotRecorded
    ? <span style={{ color: 'var(--ink-3)' }}>not recorded</span>
    : when(e.at));

  // H5 — the hub drop matches the To collect tab, and it is a DARKENED variant
  // of that tab's blue rather than the tab's blue itself. Measured rather than
  // eyeballed: the tab is `#378ADD`, which as 13px weight-600 text on white
  // gives 3.59 against the 4.5 a normal text size needs. `#1F6FBF` is the same
  // family at 5.14. The tab keeps its own colour, because a 13px chip on a
  // filled pill is not the same measurement as body text on white.
  //
  // The others, measured on the same run: landed 6.46, waiting 5.91.
  //
  // `refused` (#573, Session 79.21) is the red the transfer detail already uses
  // for "Denied — reason", so the refusal reads as the same fact in both places.
  // Measured: 8.31 on white.
  const toneColor = (tone) =>
    tone === 'landed' ? '#146B50'
      : tone === 'waiting' ? '#8A5A10'
        : tone === 'collect' ? '#1F6FBF'
          : tone === 'refused' ? '#8A2F2F'
            : '#0F0F0F';

  const head = {
    fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase',
    letterSpacing: '0.8px', textAlign: 'left', padding: '0 10px 6px 0', whiteSpace: 'nowrap',
  };
  const cell = { fontSize: 12, color: '#3A3A3A', padding: '7px 10px 7px 0', verticalAlign: 'top' };

  // WHO, WITH THE ARROW ON A CHANGE OF CUSTODY. Marcus Webb → Dana Ruiz is one
  // fact about one moment; two rows would make the reader join them up.
  const whoCell = (e) => (
    <>
      {/* #594 -- "not recorded" is a claim, so it waits until the names have
          actually been read; until then the cell says nothing. */}
      <span>{e.who || (namesLoading
        ? <span aria-label="Loading" style={{ color: 'var(--ink-3)' }}>…</span>
        : <span style={{ color: 'var(--ink-3)' }}>not recorded</span>)}</span>
      {e.handoverTo && <span> → {e.handoverTo}</span>}
      {e.driver && (
        <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 11 }}>
          driving: {e.driver}
        </span>
      )}
    </>
  );

  const viewLink = hidden > 0 && (
    <button
      onClick={() => setFull(v => !v)}
      style={{
        background: 'none', border: 'none', padding: '8px 0 0', cursor: 'pointer',
        // #581 -- 12px of #378ADD on white measures 3.59 against the 4.5 a
        // person needs, and 32px against the 44 a finger needs. #570 darkened
        // the hub line in this same component to #1F6FBF (5.14) and this link,
        // its neighbor, kept the failing blue -- one instance fixed, its
        // sibling missed.
        //
        // 44 RATHER THAN THE TIER'S 40, DELIBERATELY: gate 515_D37 H15 draws
        // this link at 44px and Colby ruled it at 79.21a's open. The tier floor
        // is a MINIMUM, not a ceiling, so a decision may sit above it.
        fontSize: 12, color: '#1F6FBF', fontWeight: 600,
        minHeight: coarse ? 44 : controlHeight(coarse, 'min'), textAlign: 'left',
      }}
    >
      {full ? 'Show less' : `View full history · ${hidden} earlier`}
    </button>
  );

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>
        History
      </div>

      {/* D20 — FOUR COLUMNS ON A DESK, STACKED BLOCKS ON A PHONE OR TABLET.
          Same four facts, same order, no horizontal scroll, and WHO and WHERE
          are never the pair dropped: they are the columns the history exists
          for. Tablets are CARRIED, which is why the stack starts at the
          workbench width rather than at the phone one. */}
      {isMobile ? (
        <div>
          {shown.map(e => {
            const fix = fixOf(e);
            const facts = (
              <>
                {/* #682 / #702 -- ON A PHONE A CORRECTED ROW IS THE DRAWN SINGLE
                    LINE (682 D14, P3, T3, T6), naming its truck only when there
                    is more than one to tell apart. */}
                {e.alert ? (
                  // #682, 79.22b.6c.2b -- S5, callout T: the sender's No or Settle
                  // as an Alert, under "Count on {truck} · {counter} · {branch}".
                  <>
                    <AlertNotice text={e.what} />
                    <div style={{ fontSize: 13, color: '#5C5C5C', lineHeight: 1.3, marginTop: 2 }}>
                      {/* The counter's name is on the question itself, so it never waits. */}
                      {[e.ref, e.who, e.where].filter(Boolean).join(' · ')}
                    </div>
                  </>
                ) : e.corrected ? (
                  <CorrectedLine c={e.corrected} at={e.at} refText={manyTrucks && e.ref ? e.ref : null} />
                ) : (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 600, color: toneColor(e.tone) }}>{e.what}</div>
                    {e.subLines && <CorrectedSubLines lines={e.subLines} />}
                    {e.detail && (
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{e.detail}</div>
                    )}
                    <div style={{ fontSize: 12, color: '#3A3A3A', marginTop: 2 }}>{whoCell(e)}</div>
                    <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 2 }}>
                      {e.where || '—'}{e.wherePlace ? ' · on the road' : ''}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                      {whenCell(e)}{e.ref ? ` · ${e.ref}` : ''}
                    </div>
                  </>
                )}
              </>
            );
            if (fix && deskWindow) {
              return (
                <div key={e.key} style={{ padding: '9px 0', borderBottom: '1px solid #F5F5F5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>{facts}</div>
                    <CorrectButton id={`correct-${e.key}`} desk coarse={coarse} blocked={fix.blocked} onPress={() => onCorrect(fix)} />
                  </div>
                  {fix.blocked && <p style={CORRECT_NOTICE}>{fix.blocked}</p>}
                </div>
              );
            }
            return (
              <div key={e.key} style={{ padding: '9px 0', borderBottom: '1px solid #F5F5F5' }}>
                {facts}
                {fix && (
                  <CorrectButton id={`correct-${e.key}`} blocked={fix.blocked} onPress={() => onCorrect(fix)} />
                )}
              </div>
            );
          })}
          {viewLink}
        </div>
      ) : (() => {
        // ON A DESK THE HISTORY STAYS GATE 553 D20's FOUR COLUMNS, and Correct
        // sits in a narrow column at its right (682 D14, callout N) -- drawn
        // only when a shown row has one for this person, so the table everyone
        // else sees is unchanged. A corrected row puts the drawn facts in the
        // four: the numbers in What, the reason beneath them, the corrector in
        // Who, the time in When.
        const fixes = shown.map(e => fixOf(e));
        const withCorrect = fixes.some(Boolean);
        return (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={head}>When</th>
                  <th style={head}>What</th>
                  <th style={head}>Who</th>
                  <th style={head}>Where</th>
                  {withCorrect && <th scope="col" aria-label="Correct" style={{ ...head, padding: '0 0 6px', width: 1 }} />}
                </tr>
              </thead>
              <tbody>
                {shown.map((e, i) => {
                  const fix = fixes[i];
                  return (
                    <React.Fragment key={e.key}>
                      <tr style={{ borderTop: '1px solid #F5F5F5' }}>
                        <td style={{ ...cell, whiteSpace: 'nowrap', color: 'var(--ink-2)' }}>{whenCell(e)}</td>
                        <td style={{ ...cell, fontWeight: 600, color: toneColor(e.tone) }}>
                          {e.alert ? <AlertNotice text={e.what} /> : e.corrected ? (
                            <>
                              {e.corrected.label} <Struck was={e.corrected.was} now={e.corrected.now} />
                              {e.corrected.item ? ` ${e.corrected.item}` : ''}
                              <span style={{ display: 'block', fontWeight: 400, fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                                {e.corrected.confirmed
                                  ? `Confirmed · ${e.corrected.confirmed.place} counted ${qtyText(e.corrected.confirmed.counted)}`
                                  : e.corrected.reason}
                              </span>
                            </>
                          ) : e.what}
                          {e.subLines && <CorrectedSubLines lines={e.subLines} desk />}
                          {/* #573 — the partly refused confirmation's sub-line, drawn as
                              approved: muted, beneath the event it qualifies. */}
                          {e.detail && <span style={{ display: 'block', fontWeight: 400, fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{e.detail}</span>}
                          {e.ref && <span style={{ display: 'block', fontWeight: 400, fontSize: 11, color: 'var(--ink-3)', marginTop: e.alert ? 4 : 0 }}>{e.ref}</span>}
                        </td>
                        <td style={cell}>{whoCell(e)}</td>
                        <td style={cell}>
                          {e.where || '—'}
                          {e.wherePlace && <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)' }}>on the road</span>}
                        </td>
                        {withCorrect && (
                          <td style={{ ...cell, padding: '7px 0 7px 6px', textAlign: 'right' }}>
                            {fix && (
                              <CorrectButton id={`correct-${e.key}`} desk coarse={coarse} blocked={fix.blocked}
                                onPress={() => onCorrect(fix)} />
                            )}
                          </td>
                        )}
                      </tr>
                      {/* S2's notice, under the row whose Correct it answers. */}
                      {fix && fix.blocked && (
                        <tr>
                          <td colSpan={withCorrect ? 5 : 4} style={{ padding: '0 0 8px' }}>
                            <p style={{ ...CORRECT_NOTICE, margin: 0 }}>{fix.blocked}</p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {viewLink}
          </>
        );
      })()}

      {/* ── #570 H13 — WHAT IS STILL STANDING, at the FOOT and outside the
          chronology ──────────────────────────────────────────────────────────
          A truck takes what fits; the rest stays at the hub and keeps appearing
          on To collect until somebody fetches it.

          THIS IS A STATE, NOT AN EVENT, which is why it sits below the list
          rather than in it — nothing happened at a particular moment to make it
          true. It is also THE ONLY LINE WITH NO ACTOR, deliberately: nobody did
          it. Putting a name against it would invent a person.

          Revision 1 of the gate missed this entirely, and it is the sentence
          D36 asked for when it wanted "at Austin since Tuesday" rather than
          "in transit". */}
      {standingLines.map(line => (
        <div key={line.key} style={{
          marginTop: 10, padding: '8px 10px', borderRadius: 8,
          background: '#F2F7FC', border: '1px solid #D6E6F5',
          fontSize: 12.5, color: '#1F6FBF', fontWeight: 600,
        }}>
          {line.text}
        </div>
      ))}
    </div>
  );
}
